import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Shield, Crosshair, Terminal, AlertTriangle, CheckCircle, Users, ChevronRight } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const PoliceJobsTab = ({ gameId, onRefresh }) => {
  const [jobs, setJobs] = useState(null);
  const [officers, setOfficers] = useState(null);
  const [executing, setExecuting] = useState(null);
  const [result, setResult] = useState(null);
  const [assigningTo, setAssigningTo] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [jRes, oRes] = await Promise.all([
        fetch(`${API}/api/police/jobs/${gameId}`),
        fetch(`${API}/api/police/officers/${gameId}`),
      ]);
      if (jRes.ok) setJobs(await jRes.json());
      if (oRes.ok) setOfficers(await oRes.json());
    } catch (e) {}
    setLoading(false);
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const executeJob = async (jobId) => {
    setExecuting(jobId); setResult(null);
    try {
      const res = await fetch(`${API}/api/police/jobs/${gameId}/execute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId }),
      });
      const d = await res.json();
      setResult({ jobId, ...d });
      load();
      if (onRefresh) onRefresh();
    } catch (e) { setResult({ jobId, error: 'Network error' }); }
    setExecuting(null);
  };

  const assignOfficer = async (officerId, operation) => {
    try {
      await fetch(`${API}/api/police/officers/${gameId}/assign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ officer_id: officerId, operation }),
      });
      load();
    } catch (e) {}
    setAssigningTo(null);
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading operations...</p>;
  if (!jobs) return null;

  const assignedToJob = (jobId) => {
    if (!officers) return [];
    return officers.officers.filter(o => o.assigned && o.assigned_to === jobId);
  };

  const availableOfficers = officers?.officers?.filter(o => o.available) || [];

  const renderJob = (job, idx) => {
    const isField = job.type === 'field';
    const jobResult = result?.jobId === job.id ? result : null;
    const assigned = assignedToJob(job.id);
    const showAssign = assigningTo === job.id;

    return (
      <motion.div key={job.id}
        initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: idx * 0.05 }}
        className="bg-[#0a0e12] border border-[#1A2535] rounded-xl overflow-hidden"
        data-testid={`police-job-${job.id}`}>
        {/* Job image */}
        {job.image && (
          <div className="relative h-28 overflow-hidden">
            <img src={job.image} alt="" className="w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e12] to-transparent" />
            <div className="absolute top-2 left-2">
              <span className={`font-['Outfit'] text-[7px] tracking-widest px-2 py-0.5 rounded border ${
                isField ? 'text-[#FF6B35] bg-[#FF6B35]/10 border-[#FF6B35]/20' : 'text-[#1E90FF] bg-[#1E90FF]/10 border-[#1E90FF]/20'
              }`}>{isField ? 'FIELD OP' : 'COMMAND'}</span>
            </div>
            <div className="absolute bottom-2 left-3">
              <h5 className="font-['Outfit'] text-sm font-bold text-[#E8E0D0] drop-shadow-lg">{job.name}</h5>
            </div>
          </div>
        )}

        <div className="p-3 space-y-2">
          <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40 leading-relaxed">{job.desc}</p>

          {/* Stats row */}
          <div className="flex flex-wrap gap-2 text-[8px] font-['Outfit']">
            <span className="text-[#FF6B35]">Energy: {job.energy}</span>
            <span className="text-[#4ADE80]">XP: +{job.reward_xp}</span>
            {job.reward_cash > 0 && <span className="text-[#C9A84C]">${job.reward_cash.toLocaleString()}</span>}
            {job.evidence_gain && <span className="text-[#1E90FF]">Evidence: +{job.evidence_gain}</span>}
            {job.evidence_req && <span className={jobs.evidence_collected >= job.evidence_req ? 'text-[#4ADE80]' : 'text-[#DC2626]'}>Req: {job.evidence_req} evidence</span>}
            <span className="text-[#E8E0D0]/20">Success: {job.success_rate}%</span>
          </div>

          {/* Assigned officers */}
          {assigned.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {assigned.map(o => (
                <span key={o.id} className="px-1.5 py-0.5 bg-[#1E90FF]/10 border border-[#1E90FF]/20 rounded text-[#1E90FF] font-['Outfit'] text-[7px]">
                  {o.name.split(' ').pop()} +{o.bonus_value}%
                </span>
              ))}
            </div>
          )}

          {/* Assign officer toggle */}
          {showAssign && (
            <div className="bg-[#111820] border border-[#1E90FF]/15 rounded-lg p-2 space-y-1">
              <p className="font-['Outfit'] text-[8px] text-[#1E90FF] tracking-widest">ASSIGN OFFICER</p>
              {availableOfficers.length === 0 ? (
                <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">No officers available</p>
              ) : (
                <div className="max-h-[100px] overflow-y-auto space-y-0.5">
                  {availableOfficers.slice(0, 8).map(o => (
                    <button key={o.id} onClick={() => assignOfficer(o.id, job.id)}
                      className="w-full flex items-center justify-between px-2 py-1 rounded hover:bg-[#1E90FF]/5 transition-colors text-left">
                      <span className="font-['Outfit'] text-[8px] text-[#E8E0D0]/60">{o.name}</span>
                      <span className="font-['Outfit'] text-[7px] text-[#1E90FF]">+{o.bonus_value}% {o.bonus_type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {jobResult && (
            <div className={`px-3 py-2 rounded-lg border ${
              jobResult.success ? 'bg-[#4ADE80]/5 border-[#4ADE80]/20' : jobResult.error ? 'bg-[#DC2626]/5 border-[#DC2626]/20' : 'bg-[#DC2626]/5 border-[#DC2626]/20'
            }`}>
              <p className={`font-['Outfit'] text-[9px] ${jobResult.success ? 'text-[#4ADE80]' : 'text-[#DC2626]'}`}>
                {jobResult.narrator || jobResult.error || (jobResult.detail)}
              </p>
              {jobResult.injuries?.length > 0 && (
                <p className="font-['Outfit'] text-[8px] text-[#DC2626]/60 mt-0.5">
                  {jobResult.injuries.map(i => `${i.officer} injured (${i.hours}h)`).join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-1.5">
            <button onClick={() => executeJob(job.id)}
              disabled={executing === job.id || !job.available}
              className={`flex-1 py-2 rounded-lg font-['Outfit'] text-[9px] tracking-wider transition-all ${
                job.available
                  ? isField ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/30 text-[#FF6B35] hover:bg-[#FF6B35]/20' : 'bg-[#1E90FF]/10 border border-[#1E90FF]/30 text-[#1E90FF] hover:bg-[#1E90FF]/20'
                  : 'bg-[#1A2535]/30 border border-[#1A2535] text-[#E8E0D0]/15'
              }`}
              data-testid={`exec-${job.id}`}>
              {executing === job.id ? 'EXECUTING...' : !job.available ? 'NEED MORE EVIDENCE' : isField ? 'DEPLOY' : 'EXECUTE'}
            </button>
            <button onClick={() => setAssigningTo(showAssign ? null : job.id)}
              className="px-3 py-2 rounded-lg border border-[#1A2535] text-[#E8E0D0]/30 font-['Outfit'] text-[9px] hover:text-[#1E90FF] hover:border-[#1E90FF]/20 transition-colors"
              data-testid={`assign-${job.id}`}>
              <Users size={12} />
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-4" data-testid="police-jobs-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1E90FF]">POLICE OPERATIONS</h3>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest">ERA {jobs.era} — OPERATION LOW TIDE</p>
        </div>
        <div className="text-right">
          <p className="font-['Outfit'] text-[8px] text-[#1E90FF]">Evidence: {jobs.evidence_collected}</p>
          <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/15">{availableOfficers.length} officers available</p>
        </div>
      </div>

      {/* Field Operations */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Crosshair size={12} className="text-[#FF6B35]" />
          <span className="font-['Outfit'] text-[#FF6B35] text-[9px] tracking-[0.3em]">FIELD OPERATIONS</span>
        </div>
        <div className="space-y-3">
          {jobs.field_ops.map((j, i) => renderJob(j, i))}
        </div>
      </div>

      {/* Command Operations */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Terminal size={12} className="text-[#1E90FF]" />
          <span className="font-['Outfit'] text-[#1E90FF] text-[9px] tracking-[0.3em]">COMMAND DECISIONS</span>
        </div>
        <div className="space-y-3">
          {jobs.command_ops.map((j, i) => renderJob(j, i + jobs.field_ops.length))}
        </div>
      </div>
    </div>
  );
};

export default PoliceJobsTab;
